import { useState } from 'react'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { DefaultVariableResult, VariableActionOption, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'

import { IDBTable } from '@/core/db'
import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useUpdateCompSettings } from '@/hooks/useUpdateCompSettings'
import { BuilderComponentSetting, BuilderComponentSettingValidation } from '@/shared/builder'
import { validatePattern } from '@/shared/form-utils'
import { VariableSelectV2 } from '@/components/VariableSelectV2/VariableSelectV2'

function assignColumnToFlags(flags: VariableSelectFeatureFlag, type: ColumnTypes) {
  if (type === ColumnTypes.DateTime) {
    flags = flags | VariableSelectFeatureFlag.CurrentDateTime
  }
  if ([ColumnTypes.Image, ColumnTypes.ImageArray].includes(type)) {
    flags = flags | VariableSelectFeatureFlag.Image
  }
  if (type === ColumnTypes.Attachment) {
    flags = flags | VariableSelectFeatureFlag.File
  }
  return flags
}

interface VariableSelectFieldSettingProps {
  id: string
  settingId: string
  variableSelectSettings: BuilderComponentSetting['variableSelectSettings']
  validation?: BuilderComponentSettingValidation
  value: any
  handleUpdate?: any // (id: number, newValues: any) => Promise<void>
  settings?: Record<string, any>
  formContext?: {
    dataSource?: string
    dbTable?: IDBTable
    settings: Record<string, any>
  }
}

const VariableSelectFieldSetting = ({
  id,
  settingId,
  settings,
  variableSelectSettings,
  value,
  validation,
  handleUpdate,
  formContext,
}: VariableSelectFieldSettingProps) => {
  const [error, setError] = useState<string>('')

  const { tables } = useDbClientContext()
  const { activeLayer, activeComponent, activeLayerElementSettings } = useActiveLayerContext()
  const activeLayerTable = activeLayer.tableId ? tables.find((t) => t.id === activeLayer.tableId) : undefined

  const activeComponentId = activeComponent?.id ?? ''
  const updateSettings = useUpdateCompSettings()

  // const validationFn = validation ? getValidationFn(validation) : undefined
  let {
    multiple = false,
    flags = VariableSelectFeatureFlag.CustomText,
    columnTypes,
    placeholder,
    inputType,
    actionType = VariableActionOption.None,
    tableSourceId,
    bindForm,
    formValidation,
    onValueChange,
    flagOrder,
  } = variableSelectSettings ?? {}

  const variableCustomTextOptions = {
    inputType: inputType ?? 'text',
    placeholder,
    validation: formValidation
      ? (customText: string) => {
          const pattern = settings?.[formValidation]
          if (pattern) {
            validatePattern(pattern, customText, (isValid, error) => {
              setError(error ?? '')
            })
          }
        }
      : undefined,
  }

  // if bindForm is true, we need to get the column id from the settings, find the type of the column, and assign the flags
  if (bindForm && settings && formContext?.dbTable) {
    // find the value of key that includes '-data-value'
    const colIdKey = Object.keys(settings).find((key) => key.includes('-data-value'))
    const colId = colIdKey ? (settings[colIdKey] as string) : undefined

    if (colId) {
      const column = formContext.dbTable.columnsMap.get(colId)
      if (column) {
        columnTypes = [column._Type]
        flags = assignColumnToFlags(flags, column._Type)
      }
    }
  }

  return (
    <VariableSelectV2
      id={settingId}
      tableId={tableSourceId ? (activeLayerElementSettings[activeComponentId]?.[tableSourceId] as string) : undefined}
      value={value as DefaultVariableResult}
      featureFlags={flags as VariableSelectFeatureFlag}
      variableColumnOptions={columnTypes ? { columnTypes, excludeColIds: [] } : undefined}
      variableCustomTextOptions={variableCustomTextOptions}
      variableActionOptions={actionType ? { type: actionType } : undefined}
      multiple={multiple}
      flagOrder={flagOrder}
      onChange={(e) => {
        if (handleUpdate) {
          handleUpdate(e)
        } else {
          updateSettings((prevSettings) => {
            const nextVal = e
            if (!!nextVal && !Array.isArray(nextVal) && onValueChange) {
              switch (nextVal.flag) {
                case VariableSelectFeatureFlag.SingleCell: {
                  if (nextVal.tbl && nextVal.col) {
                    const table = tables.find((t) => t.id === nextVal.tbl)
                    if (table) {
                      const column = table.columns.find((c) => c.id === nextVal.col)
                      if (column) {
                        onValueChange(nextVal, prevSettings, column.type)
                      }
                    }
                  }
                  break
                }
                case VariableSelectFeatureFlag.CurrentRow: {
                  if (nextVal.col) {
                    const column = activeLayerTable?.columns.find((c) => c.id === nextVal.col)
                    if (column) {
                      onValueChange(nextVal, prevSettings, column.type)
                    }
                  }
                  break
                }
                default:
                  break
              }
            }
            prevSettings[id] = nextVal
          })
        }
      }}
      error={error}
    />
  )
}

export default VariableSelectFieldSetting
